import React from 'react';
import type { GatsbyBrowser } from 'gatsby';

import 'intersection-observer';

import { MarcomThemeProvider } from './src/components/MarcomThemeProvider';
import { initSegment, initAmplitudeExperiment } from './static/js/initialScripts';
import { storeCurrentAcquisitionContext } from './src/gatsby-browser/acquisition-context-tracking';
import { GraphQLClientProvider } from './src/utils/graphql/GraphQLClientProvider';
import { BLISS_GATEWAY_ROOT_URL } from './src/utils/urlHelpers.util';
import { I18nProvider, type GatsbyPageContext, type LocaleNode } from './src/i18n/I18nProvider';
import { segment } from './static/js/joy';
import { initFallback } from './src/utils/imageFallback';

/**
 * Implement Gatsby's Browser APIs in this file.
 **/

initFallback(segment);

let isInitialized = false;

export const onInitialClientRender: GatsbyBrowser['onInitialClientRender'] = () => {
  const global = window;
  if ((global.location.search || '').replace('feature.marcom=', '').includes('feature.')) {
    // only load the query string parsing library
    // if there is a query string in the url
    import('./src/gatsby-browser/query-string-features').then((qs) => {
      qs.init();
    });
  }
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }
  global.scrollTo(0, 0);
  setTimeout(() => {
    isInitialized = true;
  }, 100);
};

export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = ({ routerProps: { location }, getSavedScrollPosition }) => {
  return isInitialized;
};

export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
  initSegment();
  segment.initTelemetry();
  segment.setDestinationHostname();
  initAmplitudeExperiment();
};

export const wrapRootElement: GatsbyBrowser['wrapRootElement'] = ({ element }) => (
  <GraphQLClientProvider endpoint={`${BLISS_GATEWAY_ROOT_URL}/graphql`}>
    <MarcomThemeProvider>{element}</MarcomThemeProvider>
  </GraphQLClientProvider>
);

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = () => {
  // Runs on initial load + after subsequent route updates
  storeCurrentAcquisitionContext();
};

export const wrapPageElement: GatsbyBrowser['wrapPageElement'] = ({ element, props }) => {
  // Extract locales from page context (injected at build time)
  const pageContext = props.pageContext as GatsbyPageContext;
  const locales = (pageContext.locales as LocaleNode[] | undefined) ?? undefined;

  return (
    <I18nProvider pageContext={pageContext} locales={locales}>
      {element}
    </I18nProvider>
  );
};
